/**
 * Making a phone photo smaller before it leaves the phone.
 *
 * WHY, WITH THE NUMBERS THAT DECIDED IT. Measured on 2026-09-09 against a real
 * 4032x3024 photograph:
 *
 *   the file a phone produces          9.0 MB
 *   what the server stores after it     396 KB
 *   server-side work                  1,191 ms
 *
 * The server was never the problem — 1.2 seconds sits underneath the SECONDS a
 * seller spends pushing nine megabytes up a mobile line. The owner's instinct
 * was to make the server work asynchronous; the measurement said the win is an
 * order of magnitude larger and one layer earlier: do not send nine megabytes.
 *
 * The same photo at 1600px is roughly 300 KB. That is the difference between a
 * seller waiting several seconds per picture and waiting less than one, on the
 * connection most of this audience posts from.
 *
 * THE SERVER STILL DOES ITS OWN WORK, unchanged. Anything a browser hands over
 * is a claim, not a fact: it can be lied about, and on a phone that refuses to
 * decode HEIC this file gives up and sends the original. So the resize is an
 * optimisation and never a guarantee — `processPhoto` remains the thing that
 * decides what we actually store.
 *
 * IT MUST NEVER STOP SOMEBODY POSTING. Every failure path returns the original
 * file. A seller whose photo will not downscale in their browser gets a slower
 * upload, which is what they have today; a seller whose post is refused because
 * a canvas threw gets nothing, which is unacceptable for a convenience.
 */

/** Nothing above this is worth sending: a listing draws at 420 CSS px. */
const MAX_EDGE = 1600;

/**
 * Below this there is nothing to gain and something to lose — re-encoding a
 * small JPEG makes it slightly worse and barely smaller.
 */
const SKIP_UNDER_BYTES = 600 * 1024;

/** High enough that a card's print quality survives a phone screen's scrutiny. */
const QUALITY = 0.85;

/**
 * Shrinks one file, or returns it untouched.
 *
 * Never throws, never rejects: callers upload whatever comes back.
 */
export async function shrinkPhoto(file) {
  try {
    if (!file || typeof file.arrayBuffer !== 'function') return file;
    // A small file, or something that is not an image, goes as it is.
    if (file.size <= SKIP_UNDER_BYTES) return file;
    if (!/^image\//.test(file.type || '')) return file;

    /*
     * `createImageBitmap` with `imageOrientation: 'from-image'`, because a phone
     * writes the rotation into EXIF rather than into the pixels. Drawing the
     * bitmap without honouring that uploads every portrait photo on its side —
     * and the EXIF block does not survive the canvas, so the server could not
     * put it back.
     */
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const { width, height } = bitmap;
    const longest = Math.max(width, height);

    // Never enlarge. A 900px photo of a card is a small photo, not a mistake.
    if (longest <= MAX_EDGE) { bitmap.close?.(); return file; }

    const scale = MAX_EDGE / longest;
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);

    /*
     * OffscreenCanvas where it exists, so the work happens off the main thread
     * and the form does not freeze mid-tap on a mid-range phone — which is the
     * device this is for. A plain canvas otherwise.
     */
    const canvas = typeof OffscreenCanvas === 'function'
      ? new OffscreenCanvas(w, h)
      : Object.assign(document.createElement('canvas'), { width: w, height: h });

    const ctx = canvas.getContext('2d');
    if (!ctx) { bitmap.close?.(); return file; }
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = canvas.convertToBlob
      ? await canvas.convertToBlob({ type: 'image/jpeg', quality: QUALITY })
      : await new Promise((res) => canvas.toBlob(res, 'image/jpeg', QUALITY));

    /*
     * And if the "smaller" one is not smaller, keep the original. Re-encoding an
     * already-efficient photo can grow it, and shipping a worse, larger file to
     * save a step would be the opposite of the point.
     */
    if (!blob || blob.size >= file.size) return file;

    // A File rather than a Blob, so the name survives into the upload and an
    // error message can still say which photo failed.
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    // Deliberately silent. See the header: a convenience must not become a
    // reason somebody cannot sell a card.
    return file;
  }
}
